import { Ref, ref } from "vue";
import axios, { AxiosError } from "axios";
import { CmsDocumentTypeMap } from "../services/cms/cms-type-configuration";
import cms from "../services/cms/cms";

export type HttpMethods = "GET" | "POST" | "PATCH" | "DELETE";

export interface Options {
  isFatal?: boolean;
  httpMethod?: HttpMethods;
  data?: any;
}

interface MyAxiosResponse<T> {
  data: Ref<T | null>;
  errorStatus: Ref<number | null>;
  promise: Promise<T>;
  refreshData: () => Promise<T>;
}

function useErrorPage(statusCode: number): void {
  if (process.env.NODE_ENV === 'development') {
    return;
  }
  switch (statusCode) {
    case 403:
      window.location.href = "/errorpages/NotAuthorised";
      break;
    case 404:
      window.location.href = "/errorpages/NotFound";
      break;
    default:
      window.location.href = "/errorpages/Error";
      break;
  }
}

export function useAxios<T>(url: string, options: Options = {}): MyAxiosResponse<T> {
  const {
    isFatal,
    httpMethod,
    data: axiosData,
  } = { isFatal: true, httpMethod: "GET", data: null, ...options } as Options;

  const data: Ref<T | null> = ref(null);
  const errorStatus: Ref<number | null> = ref(null);

  async function doAxios(): Promise<T> {
    data.value = null;
    errorStatus.value = null;

    try {
      let response;
      switch (httpMethod) {
        case "POST":
          response = await axios.post(url, axiosData);
          break;
        case "PATCH":
          response = await axios.patch(url, axiosData);
          break;
        case "DELETE":
          response = await axios.delete(url, { data: axiosData });
          break;
        case "GET":
        default:
          response = await axios.get(url);
          break;
      }

      data.value = await response.data;
      return response.data;
    } catch (e: any) {
      const status = (e as AxiosError).response?.status || 0;
      errorStatus.value = status;

      if (isFatal) {
        useErrorPage(errorStatus.value);
      }

      throw status;
    }
  }

  const promise = doAxios();
  const refreshData = doAxios;

  return {
    data,
    errorStatus,
    promise,
    refreshData,
  };
}

export function useCmsGetByUid<X extends keyof CmsDocumentTypeMap>(
  type: X,
  rawUid: string,
  options: Options = {},
): MyAxiosResponse<X> {
  const { isFatal } = { isFatal: true, ...options };
  const data: Ref<X | null> = ref(null);
  const errorStatus: Ref<number | null> = ref(null);

  async function doGetByUid(): Promise<X> {
    data.value = null;
    errorStatus.value = null;

    let response;
    try {
      response = await cms.getByUid(type, rawUid);
      data.value = await response;
      return response;
    } catch (e: any) {
      const status = (e as AxiosError).response?.status || 0;
      errorStatus.value = status;

      if (isFatal) {
        useErrorPage(errorStatus.value);
      }

      throw status;
    }
  }

  const promise = doGetByUid();
  const refreshData = doGetByUid;

  return {
    data,
    errorStatus,
    promise,
    refreshData,
  };
}

export default useAxios;
